import { adjustedCategory, categoryLabel } from "../config/productCategories";
import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, ArrowRight, Loader2, ExternalLink, Flame } from "lucide-react";
import type { Lead } from "../services/LeadsService";
import { getProductImageUrl } from "../utils/imageUtils";
import { getAffiliateLink } from "../services/ProductsService";
import {
  formatPrice,
  getLeadDiscountPercent,
  getLeadExternalLink,
  getLeadOriginalPrice,
  getLeadStockSearchSku,
  resolveWalmartIds,
} from "../pages/LeadsPage.utils";
import LeadSaveButton from "./buttons/LeadSaveButton";
import SetAlertButton from "./buttons/SetAlertButton";
import CheckStockButton from "./buttons/CheckStockButton";
import { useTranslation } from "../context/LanguageContext";

type Translate = (key: string, params?: Record<string, string | number>) => string;

interface TopLeadsViewProps {
  leads: Lead[];
  hiddenCategories?: string[];
  categoryLabels?: Record<string, string>;
  totalAllCount: number;
  onSeeAll: () => void;
  onSelectCategory: (category: string) => void;
  hoursWindow?: number;
  retailer: string;
  onCheckStockSearch: (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => void | Promise<void>;
  /** Rendered above the rails inside the scroll area, so it scrolls away with the feed. */
  header?: React.ReactNode;
  /** "grid" lays each section out as cards in rows; "list" keeps the sideways rails. */
  layoutMode?: "list" | "grid";
}

const MIN_ROW_ITEMS = 3;

interface RankedLead {
  lead: Lead;
  discount: number;
  lastUpdated: number;
}

const buildRankedLeads = (leads: Lead[]): RankedLead[] => {
  const out: RankedLead[] = [];
  for (const lead of leads) {
    if (!lead.id || !Number.isFinite(lead.lowestPrice) || lead.lowestPrice <= 0) continue;
    const orig = getLeadOriginalPrice(lead.msrp, lead.onlinePrice);
    out.push({
      lead,
      discount: getLeadDiscountPercent(orig, lead.lowestPrice),
      lastUpdated: lead.lastUpdated || 0,
    });
  }
  return out;
};

const timeAgo = (ts: number, t: Translate) => {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("leads.badgeJustNow");
  if (mins < 60) return t("leads.badgeMinutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("leads.badgeHours", { count: hrs });
  return t("leads.badgeDays", { count: Math.floor(hrs / 24) });
};

const ALERT_RETAILERS = new Set(["walmart", "home_depot", "dollar_general"]);
const AFFILIATE_RETAILERS = new Set(["walmart", "walmart_online", "target", "target_online"]);

const TopLeadsView: React.FC<TopLeadsViewProps> = ({
  leads,
  hiddenCategories = [],
  categoryLabels = {},
  totalAllCount,
  onSeeAll,
  onSelectCategory,
  hoursWindow = 24,
  retailer,
  onCheckStockSearch,
  header,
  layoutMode = "list",
}) => {
  const { t } = useTranslation();
  const supportsAlerts = ALERT_RETAILERS.has(retailer);
  const grid = layoutMode === "grid";

  const stats = useMemo(() => {
    const cutoff = Date.now() - hoursWindow * 3600 * 1000;
    const ranked = buildRankedLeads(leads);
    const recent = ranked.filter((r) => r.lastUpdated > cutoff);
    const withDiscount = recent.filter((r) => r.discount > 0);
    const byDiscount = [...withDiscount].sort((a, b) => b.discount - a.discount);
    const byNewest = [...recent].sort((a, b) => b.lastUpdated - a.lastUpdated);
    const avgDiscount = withDiscount.length
      ? Math.round(withDiscount.reduce((s, r) => s + r.discount, 0) / withDiscount.length)
      : 0;

    return {
      recentCount: recent.length,
      avgDiscount,
      featured: byDiscount.slice(0, 10).map((r) => r.lead),
      picks: byDiscount.slice(10, 22).map((r) => r.lead),
      justDropped: byNewest.slice(0, 10).map((r) => r.lead),
    };
  }, [leads, hoursWindow]);

  const topCategoryRails = useMemo(() => {
    const cutoff = Date.now() - hoursWindow * 3600 * 1000;
    const ranked = buildRankedLeads(leads);
    const recent = ranked.filter((r) => r.lastUpdated > cutoff);
    const hasRecent = recent.length > 0;
    const pool = hasRecent
      ? recent.filter((r) => r.discount > 0)
      : ranked.filter((r) => r.discount > 0);
    const byCat = new Map<string, RankedLead[]>();
    const hidden = new Set(hiddenCategories);
    for (const r of pool) {
      const c = adjustedCategory(r.lead);
      if (!c || hidden.has(c)) continue;
      byCat.set(c, [...(byCat.get(c) || []), r]);
    }
    return Array.from(byCat.entries())
      .map(([name, list]) => ({
        name,
        leads: [...list].sort((a, b) => b.discount - a.discount).slice(0, 12).map((x) => x.lead),
      }))
      .filter((s) => s.leads.length >= MIN_ROW_ITEMS)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [leads, hiddenCategories, hoursWindow]);

  const hasRecent = stats.recentCount > 0;

  const fallback = useMemo(() => {
    if (hasRecent) return null;
    const ranked = buildRankedLeads(leads).sort((a, b) => b.discount - a.discount);
    return {
      featured: ranked.slice(0, 10).map((r) => r.lead),
      picks: ranked.slice(10, 22).map((r) => r.lead),
    };
  }, [hasRecent, leads]);

  const featured = hasRecent ? stats.featured : fallback?.featured || [];
  const picks = hasRecent ? stats.picks : fallback?.picks || [];

  const headerTitle = hasRecent ? t("leads.todaysTopLeads") : t("leads.bestLeadsNow");
  const headerSubtitle = hasRecent
    ? t("leads.freshSubtitle", { count: stats.recentCount, pct: stats.avgDiscount })
    : t("leads.bestMarkdownsNow");

  const hasRows = featured.length >= MIN_ROW_ITEMS || topCategoryRails.length > 0 || picks.length >= MIN_ROW_ITEMS || (hasRecent && stats.justDropped.length >= MIN_ROW_ITEMS);

  return (
    <div className={`w-full h-full overflow-y-auto scrollbar-modern overscroll-contain pb-20 ${grid ? "bg-[#F9F8FC] dark:bg-transparent" : ""}`}>
      {header}
      {featured.length >= MIN_ROW_ITEMS && (
        <LeadRail
          title={headerTitle}
          subtitle={headerSubtitle}
          large
          action={totalAllCount > 0 ? { label: t("leads.seeAllCount", { count: totalAllCount }), onClick: onSeeAll } : undefined}
          leads={featured}
          retailer={retailer}
          supportsAlerts={supportsAlerts}
          hottestFirst
          onCheckStockSearch={onCheckStockSearch}
          grid={grid}
        />
      )}
      {!hasRows && (
        <div className="px-4 pt-8 pb-2 text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t("leads.moreOnTheWay")}</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-dark-400">{t("leads.browseAllHint")}</p>
        </div>
      )}

      {topCategoryRails.map(({ name, leads: catLeads }) => (
        <div key={name} className="mt-6">
          <LeadRail
            title={t("leads.topCategory", { category: categoryLabels[name]?.trim() || categoryLabel(name) })}
            action={{ label: t("leads.seeAll"), onClick: () => onSelectCategory(name) }}
            leads={catLeads}
            retailer={retailer}
            supportsAlerts={supportsAlerts}
            onCheckStockSearch={onCheckStockSearch}
            grid={grid}
          />
        </div>
      ))}

      {picks.length >= MIN_ROW_ITEMS && (
        <div className="mt-6">
          <LeadRail
            title={t("leads.moreTopPicks")}
            leads={picks}
            retailer={retailer}
            supportsAlerts={supportsAlerts}
            onCheckStockSearch={onCheckStockSearch}
            grid={grid}
          />
        </div>
      )}

      {hasRecent && stats.justDropped.length >= MIN_ROW_ITEMS && (
        <div className="mt-6">
          <LeadRail
            title={t("leads.justDropped")}
            subtitle={t("leads.newestLeadsFirst")}
            leads={stats.justDropped}
            retailer={retailer}
            supportsAlerts={supportsAlerts}
            getTimeBadge={(l) => (l.lastUpdated ? timeAgo(l.lastUpdated, t) : undefined)}
            onCheckStockSearch={onCheckStockSearch}
            grid={grid}
          />
        </div>
      )}

      <button
        onClick={onSeeAll}
        className="w-full flex items-center justify-center gap-1.5 py-4 mt-7 text-[14px] font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer"
      >
        <span>{totalAllCount > 0 ? t("leads.browseAllLeadsCount", { count: totalAllCount }) : t("leads.browseAllLeads")}</span>
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default TopLeadsView;

const SectionHeader = memo(function SectionHeader({
  title,
  subtitle,
  eyebrow,
  action,
  large,
  navigation,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  action?: { label: string; onClick: () => void };
  large?: boolean;
  navigation?: React.ReactNode;
}) {
  return (
    <div className={`flex items-end gap-3 px-4 mb-2.5 ${large ? "pt-2 mb-3.5" : ""}`}>
      <div className="flex-1 min-w-0">
        {eyebrow ? (
          <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-gray-500 dark:text-dark-400 mb-1">
            {eyebrow}
          </div>
        ) : null}
        <h2 className={`font-extrabold text-gray-900 dark:text-white ${large ? "text-[24px] leading-7" : "text-[17px]"}`}>
          {title}
        </h2>
        {subtitle ? (
          <div className="text-[12px] text-gray-500 dark:text-dark-400 mt-1">{subtitle}</div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
      {action ? (
        <button
          onClick={action.onClick}
          className="flex items-center gap-0.5 py-1 text-[13px] font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 cursor-pointer"
        >
          <span>{action.label}</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      ) : null}
      {navigation}
      </div>
    </div>
  );
});

interface LeadRailProps {
  title: string;
  subtitle?: string;
  large?: boolean;
  action?: { label: string; onClick: () => void };
  leads: Lead[];
  retailer: string;
  supportsAlerts: boolean;
  hottestFirst?: boolean;
  getTimeBadge?: (lead: Lead) => string | undefined;
  onCheckStockSearch: (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => void | Promise<void>;
  grid?: boolean;
}

// Grid rows: up to 8 cards, trimmed to an even count so phones show full rows of two.
const GRID_LIMIT = 8;

const LeadRail: React.FC<LeadRailProps> = ({
  title,
  subtitle,
  large,
  action,
  leads,
  retailer,
  supportsAlerts,
  hottestFirst = false,
  getTimeBadge,
  onCheckStockSearch,
  grid = false,
}) => {
  const { t } = useTranslation();
  const viewport = useRef<HTMLDivElement>(null);
  const viewportId = useId();
  const [navigation, setNavigation] = useState({ previous: false, next: false });

  const updateNavigation = useCallback(() => {
    const element = viewport.current;
    if (!element) return;
    const previous = element.scrollLeft > 1;
    const next = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setNavigation(current => current.previous === previous && current.next === next ? current : { previous, next });
  }, []);

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    // Reset when the retailer or collection changes, and recalculate on resize.
    element.scrollLeft = 0;
    updateNavigation();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateNavigation) : null;
    observer?.observe(element);
    window.addEventListener("resize", updateNavigation);
    return () => { observer?.disconnect(); window.removeEventListener("resize", updateNavigation); };
  }, [leads, retailer, updateNavigation]);

  const scrollPage = (direction: number) => {
    const element = viewport.current;
    if (!element) return;
    const card = element.firstElementChild;
    const cardStep = (card?.getBoundingClientRect().width || 168) + 10;
    const visibleCards = Math.max(1, Math.floor((element.clientWidth - 22) / cardStep));
    element.scrollBy({
      left: direction * visibleCards * cardStep,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };

  const hasOverflow = navigation.previous || navigation.next;
  const arrowClass = "inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default dark:border-dark-600 dark:bg-dark-800 dark:text-dark-200 dark:hover:bg-dark-700 focus-visible:outline-2 focus-visible:outline-primary-500 cursor-pointer";

  if (grid) {
    const cards = leads.slice(0, GRID_LIMIT);
    const even = cards.length > 1 && cards.length % 2 === 1 ? cards.slice(0, -1) : cards;
    return (
      <section aria-label={title}>
        <SectionHeader title={title} subtitle={subtitle} action={action} large={large} />
        <div className="grid grid-cols-2 gap-2.5 px-3 pt-1 pb-2 sm:grid-cols-3 lg:grid-cols-4">
          {even.map((lead, i) => (
            <DealCard
              key={`${lead.id || "lead"}-${i}`}
              lead={lead}
              retailer={retailer}
              supportsAlerts={supportsAlerts}
              hottest={hottestFirst && i === 0}
              timeBadge={getTimeBadge?.(lead)}
              onCheckStockSearch={onCheckStockSearch}
              fluid
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section aria-label={title}>
      <SectionHeader
        title={title}
        subtitle={subtitle}
        action={action}
        large={large}
        navigation={hasOverflow && (
          <div className="hidden items-center gap-1.5 sm:flex">
            <button aria-label={t("leads.previousProducts", { title })} aria-controls={viewportId} disabled={!navigation.previous} onClick={() => scrollPage(-1)} className={arrowClass}><ChevronLeft size={16} /></button>
            <button aria-label={t("leads.nextProducts", { title })} aria-controls={viewportId} disabled={!navigation.next} onClick={() => scrollPage(1)} className={arrowClass}><ChevronRight size={16} /></button>
          </div>
        )}
      />
      <div
        id={viewportId}
        ref={viewport}
        role="group"
        aria-label={t("leads.railProducts", { title })}
        tabIndex={0}
        onScroll={updateNavigation}
        onKeyDown={event => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            scrollPage(event.key === "ArrowLeft" ? -1 : 1);
          }
        }}
        className="flex items-stretch gap-2.5 overflow-x-auto overflow-y-hidden overscroll-x-contain scrollbar-hide px-4 pt-1 pb-2 snap-x scroll-px-4 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500"
      >
        {leads.map((lead, i) => (
          <DealCard
            key={`${lead.id || "lead"}-${i}`}
            lead={lead}
            retailer={retailer}
            supportsAlerts={supportsAlerts}
            hottest={hottestFirst && i === 0}
            timeBadge={getTimeBadge?.(lead)}
            onCheckStockSearch={onCheckStockSearch}
          />
        ))}
      </div>
    </section>
  );
};

interface DealCardProps {
  lead: Lead;
  retailer: string;
  supportsAlerts: boolean;
  hottest?: boolean;
  timeBadge?: string;
  onCheckStockSearch: LeadRailProps["onCheckStockSearch"];
  /** Fill the grid cell instead of the rail's fixed width. */
  fluid?: boolean;
}

const DealCard = memo(function DealCard({
  lead,
  retailer,
  supportsAlerts,
  hottest = false,
  timeBadge,
  onCheckStockSearch,
  fluid = false,
}: DealCardProps) {
  const { t } = useTranslation();
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const orig = getLeadOriginalPrice(lead.msrp, lead.onlinePrice);
  const discount = getLeadDiscountPercent(orig, lead.lowestPrice);
  const isWalmart = retailer === "walmart" || retailer === "walmart_online";
  const hasAffiliate = AFFILIATE_RETAILERS.has(retailer);
  const resolved = resolveWalmartIds(lead, retailer);
  const stockSearchSku = getLeadStockSearchSku(lead, retailer);
  const externalLink = getLeadExternalLink(
    retailer,
    isWalmart ? { id: resolved.sku, sku: resolved.sku } : lead
  );

  const handleOpen = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (affiliateLoading) return;
      const tab = window.open("about:blank", "_blank");
      if (!hasAffiliate) {
        if (tab) tab.location.href = externalLink.url;
        else window.location.href = externalLink.url;
        return;
      }
      setAffiliateLoading(true);
      try {
        const trackingUrl = await getAffiliateLink(
          resolved.sku || resolved.upc,
          externalLink.url,
          retailer
        );
        if (tab) tab.location.href = trackingUrl;
        else window.location.href = trackingUrl;
      } catch {
        if (tab) tab.location.href = externalLink.url;
        else window.location.href = externalLink.url;
      } finally {
        setAffiliateLoading(false);
      }
    },
    [affiliateLoading, externalLink.url, hasAffiliate, resolved.sku, resolved.upc, retailer]
  );

  const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  const cardClass = hottest
    ? "border-purple-200 dark:border-purple-400/40 shadow-[0_2px_12px_rgba(106,13,173,0.10)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)]"
    : "border-gray-900/5 dark:border-white/[0.07] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_2px_10px_rgba(16,24,40,0.05)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)]";

  return (
    <div
      onClick={handleOpen}
      className={`group cursor-pointer ${fluid ? "w-full min-w-0" : "w-[168px] flex-shrink-0 snap-start"} rounded-[18px] border bg-white dark:bg-[#26221f] overflow-hidden flex flex-col ${cardClass} hover:shadow-md transition-shadow`}
    >
      <div className="relative m-1.5 aspect-square rounded-[13px] border border-[#ECEAF2] bg-white flex-shrink-0 overflow-hidden dark:border-white/10">
        {lead.image ? (
          <img
            src={getProductImageUrl(lead.image, "200")}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain p-2"
          />
        ) : null}

        {discount > 0 && (
          <div className="absolute bottom-[7px] left-[7px] flex items-center gap-1 px-[7px] py-[3px] rounded-[7px] bg-red-600 shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
            {discount > 70 && <Flame className="w-2.5 h-2.5 text-white" />}
            <span className="text-[10px] font-extrabold tracking-[0.02em] text-white">
              {t("leads.percentOff", { pct: discount })}
            </span>
          </div>
        )}

        {timeBadge && (
          <div className="absolute bottom-[7px] right-[7px] flex items-center gap-1 px-1.5 py-[2.5px] rounded-full bg-gray-900/70 text-white text-[10px] font-bold">
            <Clock className="w-2.5 h-2.5" />
            <span>{timeBadge}</span>
          </div>
        )}

        <div
          onClick={stopPropagation}
          className="absolute top-1.5 right-1.5 rounded-full bg-white/95 shadow-[0_1px_4px_rgba(0,0,0,0.12)]"
        >
          <LeadSaveButton leadId={lead.id} retailer={retailer} compact />
        </div>
      </div>

      <div className="px-[11px] pt-0.5 pb-2 flex-1 flex flex-col gap-[3px]">
        <div
          className="text-[13px] font-medium leading-[17px] text-gray-900 dark:text-white overflow-hidden"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 34 }}
        >
          {lead.title}
        </div>
        <div className="flex items-baseline flex-wrap gap-1.5">
          <span className="text-[17px] font-extrabold tracking-tight text-emerald-600 dark:text-emerald-400">
            {formatPrice(lead.lowestPrice)}
          </span>
          {orig > lead.lowestPrice && (
            <span className="text-[12px] text-gray-500 dark:text-dark-400 line-through">
              {formatPrice(orig)}
            </span>
          )}
        </div>
      </div>

      <div
        onClick={stopPropagation}
        className="flex items-stretch gap-1.5 px-2 pt-0.5 pb-2"
      >
        {supportsAlerts && (
          <SetAlertButton
            productUpc={resolved.upc}
            productSku={resolved.sku || resolved.upc}
            productName={lead.title}
            productImage={lead.image}
            productCategory={adjustedCategory(lead)}
            msrp={lead.msrp}
            currentPrice={lead.lowestPrice}
            className="!h-[30px] !flex-1 !min-w-0 !inline-flex !items-center !justify-center !gap-1 !rounded-lg !border-0 !shadow-none !text-[12px] !font-semibold !py-0 !px-2 !bg-violet-50 dark:!bg-violet-500/10 !text-violet-700 dark:!text-violet-300 hover:!bg-violet-100 dark:hover:!bg-violet-500/20"
          />
        )}
        <CheckStockButton
          productSku={stockSearchSku}
          productUpc={resolved.upc}
          onSearch={onCheckStockSearch}
          className="!h-[30px] !flex-1 !min-w-0 !inline-flex !items-center !justify-center !gap-1 !rounded-lg !border-0 !shadow-none !text-[12px] !font-semibold !py-0 !px-2 !bg-gray-100 dark:!bg-white/[0.06] !text-gray-700 dark:!text-dark-200 hover:!bg-gray-200 dark:hover:!bg-white/10"
        />
        {!supportsAlerts && (
          <button
            onClick={handleOpen}
            disabled={affiliateLoading}
            className="!h-[30px] !flex-1 !min-w-0 inline-flex items-center justify-center gap-1 rounded-lg text-[12px] font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-dark-200 dark:hover:bg-white/10 disabled:opacity-60 px-2"
          >
            {affiliateLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            <span className="truncate">{t("leads.visit")}</span>
          </button>
        )}
      </div>
    </div>
  );
});
